import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal";
import { Attachment } from "../../../../interfaces/transaction.interface";
import { ATTACHMENT, VALIDATION_FIELD_MAPPING } from "../../../../utils/appConstant";

export interface UploadQueueItem {
  localId: string;
  file: File;
  status: 'uploading' | 'error';
  uploadedChunks: number;
  totalChunks: number;
  error?: string;
}

interface AttachmentsTabProps {
  attachments: Attachment[];
  uploadQueue: UploadQueueItem[];
  view?: boolean;
  removingId?: number | null;
  openingId?: number | null;
  onFilesSelected: (fileList: FileList) => void;
  onRemoveAttachment: (attachmentId: number) => void;
  onOpenAttachment: (attachment: Attachment) => void;
  onRetryQueueItem: (localId: string) => void;
  onRemoveQueueItem: (localId: string) => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

const AttachmentsTab: React.FC<AttachmentsTabProps> = ({
  attachments,
  uploadQueue,
  view,
  removingId,
  openingId,
  onFilesSelected,
  onRemoveAttachment,
  onOpenAttachment,
  onRetryQueueItem,
  onRemoveQueueItem,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFilesSelected(e.target.files);
    }
    e.target.value = '';
  };

  const handleConfirmRemove = () => {
    if (confirmRemoveId !== null) onRemoveAttachment(confirmRemoveId);
    setConfirmRemoveId(null);
  };

  const isEmpty = attachments.length === 0 && uploadQueue.length === 0;

  return (
    <div className="card-body">
      {!view && (
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              id={VALIDATION_FIELD_MAPPING.attachments}
              name={VALIDATION_FIELD_MAPPING.attachments}
              accept={ATTACHMENT.allowedExtensions.map((ext) => `.${ext}`).join(',')}
              className="d-none"
              onChange={handleFileInputChange}
            />
            <Button
              icon={<i className="ri-attachment-2 ri-20px me-1_5"></i>}
              text={t('transactions.addEdit.pageTexts.form.attachments.addButton')}
              className="btn btn-primary h-100 w-100"
              onClick={() => fileInputRef.current?.click()}
            />
          </div>
        </div>
      )}

      {!isEmpty && <hr className="my-6" />}

      <div className="row g-4">
        {isEmpty && (
          <p className="dataTables_empty text-center">
            {t('transactions.addEdit.pageTexts.form.attachments.empty')}
          </p>
        )}

        {uploadQueue.map((item) => (
          <div className="col-md-3" key={item.localId}>
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center mb-2">
                  <i className="ri-image-line ri-20px me-2"></i>
                  <span className="text-truncate">{item.file.name}</span>
                </div>

                {item.status === 'uploading' && (
                  <div className="progress" style={{ height: '6px' }}>
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ width: `${Math.round((item.uploadedChunks / item.totalChunks) * 100)}%` }}
                    />
                  </div>
                )}

                {item.status === 'uploading' && (
                  <small className="text-muted">{t('transactions.addEdit.pageTexts.form.attachments.uploading')}</small>
                )}

                {item.status === 'error' && (
                  <>
                    <small className="text-danger d-block mb-2">
                      {item.error || t('transactions.addEdit.pageTexts.form.attachments.failed')}
                    </small>
                    <Button
                      className="btn btn-sm btn-outline-primary me-2"
                      text={t('transactions.addEdit.pageTexts.form.attachments.retryButton')}
                      onClick={() => onRetryQueueItem(item.localId)}
                    />
                    <Button
                      className="btn btn-sm btn-outline-secondary"
                      text={t('transactions.addEdit.pageTexts.form.attachments.removeButton')}
                      onClick={() => onRemoveQueueItem(item.localId)}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        ))}

        {attachments.map((attachment) => (
          <div className="col-md-3" key={attachment.id}>
            <div className="card">
              <div className="card-body">
                {!view && (
                  <Button
                    icon={<i className="ri-delete-bin-6-line ri-20px text-danger"></i>}
                    className="btn btn-sm position-absolute end-10 top-10 shadow-none border-0 p-0"
                    ariaLabel="Remove attachment"
                    loading={removingId === attachment.id}
                    onClick={() => setConfirmRemoveId(attachment.id)}
                  />
                )}
                <a
                  href="#"
                  aria-disabled={openingId === attachment.id}
                  onClick={(e) => {
                    e.preventDefault();
                    if (openingId === attachment.id) return;
                    onOpenAttachment(attachment);
                  }}
                  className="d-flex align-items-center mb-2 text-body"
                >
                  <i className="ri-download-2-line ri-20px me-2"></i>
                  <span className="text-truncate">{attachment.originalFilename}</span>
                </a>
                <small className="text-muted">{formatFileSize(attachment.sizeBytes)}</small>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={confirmRemoveId !== null}
        onClose={() => setConfirmRemoveId(null)}
        title={t('transactions.addEdit.pageTexts.form.attachments.confirmModal.title')}
        content={t('transactions.addEdit.pageTexts.form.attachments.confirmModal.content')}
        contentClassName="text-center"
        maxWidth="sm"
        action={
          <div className="d-flex gap-3">
            <Button
              className="btn btn-outline-secondary"
              text={t('transactions.addEdit.pageTexts.form.attachments.confirmModal.cancelBtn')}
              onClick={() => setConfirmRemoveId(null)}
            />
            <Button
              className="btn btn-danger"
              text={t('transactions.addEdit.pageTexts.form.attachments.confirmModal.deleteBtn')}
              onClick={handleConfirmRemove}
            />
          </div>
        }
      />
    </div>
  );
};

export default AttachmentsTab;
